import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getComicsByCategory } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comicId = Number(id);
  const comic = getComicById(comicId);

  if (!comic) {
    return <NotFound />;
  }

  const relatedComics = getComicsByCategory(comic.categorySlug)
    .filter((c) => c.id !== comic.id)
    .slice(0, 4);

  const chapterList = Array.from({ length: Math.min(comic.chapters, 50) }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(2025, 0, 1 + i * 7).toISOString().split('T')[0],
  }));

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://heiliaoapp.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: comic.category,
        item: `http://heiliaoapp.cn/category/${comic.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: `http://heiliaoapp.cn/comic/${comic.id}`,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 无删减韩国漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2024-01-01',
    dateModified: comic.updateDate,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: comic.category, path: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />

            <article className="py-8">
              <div className="flex flex-col md:flex-row gap-8 md:gap-12">
                <div className="md:w-[320px] flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="relative overflow-hidden rounded-2xl aspect-[3/4] bg-background-100">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - 韩国漫画无删减`}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute top-3 right-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-label ${
                          comic.status === '连载中'
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-background-950/70 text-foreground-200'
                        }`}>
                          {comic.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                    {comic.title}
                  </h1>
                  <p className="mt-1 text-sm text-foreground-500">{comic.altTitle}</p>

                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i> {comic.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-paint-brush-line"></i> {comic.artist}
                    </span>
                    <span className="flex items-center gap-1 text-primary-500">
                      <i className="ri-star-fill"></i> {comic.rating}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {comic.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-heart-line"></i> {comic.favorites}
                    </span>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-xs font-label bg-accent-100 text-accent-700">
                      {comic.category}
                    </span>
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs bg-background-200 text-foreground-600"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 p-5 rounded-xl bg-background-100">
                    <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {comic.description}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-label hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                      开始阅读 第1话
                    </button>
                    <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-label hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer">
                      <i className="ri-bookmark-line mr-1"></i> 加入书架
                    </button>
                  </div>
                </div>
              </div>

              <section className="mt-12">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                  章节列表
                  <span className="text-sm font-normal text-foreground-500 ml-2 font-label">
                    共 {comic.chapters} 话
                  </span>
                </h2>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {chapterList.map((chapter) => (
                    <button
                      key={chapter.number}
                      className="flex flex-col items-start p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-all text-left cursor-pointer"
                    >
                      <span className="text-sm font-medium text-foreground-900 line-clamp-1">
                        {chapter.title}
                      </span>
                      <span className="text-xs text-foreground-500 mt-1">{chapter.date}</span>
                    </button>
                  ))}
                </div>
              </section>
            </article>

            {relatedComics.length > 0 && (
              <section className="py-12 border-t border-background-200/70">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                  同类推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}